import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeRowModal,
  createRow,
  updateRow,
  selectEditingRow,
  selectLoading,
  selectSchema as selectFullSchema,
  selectForeignKeyOptions
} from '../../features/adminSlice';


const AdminRowModal = ({ schema, primaryKeys, tableName }) => {
  const dispatch = useDispatch();
  const editingRow = useSelector(selectEditingRow);
  const loading = useSelector(selectLoading);
  const fullSchema = useSelector(selectFullSchema);
  const foreignKeyOptions = useSelector(selectForeignKeyOptions);

  const isEditing = !!editingRow;

  // Check if a column has an auto-generated default (sequence or timestamp)
  const isAutoGenerated = (columnName) => {
    const col = schema.find((c) => c.column_name === columnName);
    if (!col?.column_default) return false;
    const defaultVal = col.column_default.toLowerCase();
    return defaultVal.includes('nextval') || defaultVal.includes('current_timestamp');
  };

  // Build a comma-separated row ID string for composite keys
  const buildRowId = (row) => {
    return primaryKeys.map((pk) => row[pk]).join(',');
  };

  // Format primary key display for modal header
  const formatPrimaryKeyDisplay = (row) => {
    return primaryKeys.map((pk) => `${pk}: ${row[pk]}`).join(', ');
  };

  // Initialize form data
  const [formData, setFormData] = useState({});
  const [jsonErrors, setJsonErrors] = useState({});

  // Initialize form data when modal opens
  useEffect(() => {
    if (editingRow) {
      // Editing existing row - populate with current values
      const data = {};
      schema.forEach((col) => {
        const value = editingRow[col.column_name];
        if (typeof value === 'object' && value !== null) {
          data[col.column_name] = JSON.stringify(value, null, 2);
        } else {
          data[col.column_name] = value ?? '';
        }
      });
      setFormData(data);
    } else {
      // Creating new row - initialize with empty values
      const data = {};
      schema.forEach((col) => {
        // Skip auto-generated columns (sequences, timestamps)
        if (isAutoGenerated(col.column_name)) return;
        data[col.column_name] = '';
      });
      setFormData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRow, schema]);

  // Handle form field change
  const handleChange = (columnName, value) => {
    setFormData((prev) => ({
      ...prev,
      [columnName]: value
    }));

    // Clear JSON error for this field
    if (jsonErrors[columnName]) {
      setJsonErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[columnName];
        return newErrors;
      });
    }
  };

  // Validate JSON fields
  const validateJsonField = (columnName, value) => {
    if (!value || value.trim() === '') return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      setJsonErrors((prev) => ({
        ...prev,
        [columnName]: 'Invalid JSON syntax'
      }));
      return false;
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate JSON fields
    const jsonFields = schema.filter((col) => col.data_type === 'jsonb');
    let hasErrors = false;
    jsonFields.forEach((col) => {
      if (!validateJsonField(col.column_name, formData[col.column_name])) {
        hasErrors = true;
      }
    });

    if (hasErrors) return;

    // Prepare data for submission
    const data = {};
    Object.entries(formData).forEach(([key, value]) => {
      // Skip empty values for create
      if (!isEditing && (value === '' || value === null || value === undefined)) {
        return;
      }

      // Skip auto-generated columns (sequences, timestamps)
      if (isAutoGenerated(key)) {
        return;
      }

      const col = schema.find((c) => c.column_name === key);
      if (col?.data_type === 'jsonb') {
        // Parse JSON fields
        try {
          data[key] = value ? JSON.parse(value) : null;
        } catch {
          data[key] = null;
        }
      } else if (col?.data_type === 'integer') {
        // Parse integer fields
        data[key] = value === '' ? null : parseInt(value, 10);
      } else {
        data[key] = value === '' ? null : value;
      }
    });

    if (isEditing) {
      dispatch(updateRow({
        tableName,
        rowId: buildRowId(editingRow),
        data
      }));
    } else {
      dispatch(createRow({
        tableName,
        data
      }));
    }
  };

  // Handle close
  const handleClose = () => {
    dispatch(closeRowModal());
  };

  // Render input field based on data type
  const renderField = (col) => {
    const value = formData[col.column_name] ?? '';
    const isDisabled = isAutoGenerated(col.column_name);

    if (fullSchema?.fk_config?.[col.column_name]) {
      const fkConfig = fullSchema.fk_config[col.column_name];
      const rows = foreignKeyOptions[fkConfig.table] || [];
      const pkCol = fullSchema?.primary_keys?.[fkConfig.table]?.[0] || 'id';
      const matchCol = fkConfig.value_col || pkCol;
      const isNullable = col.is_nullable === true || col.is_nullable === 'YES';

      // Deduplicate by matchCol, joining multiple labels with ", "
      const seen = new Map();
      rows.forEach((r) => {
        const val = r[matchCol];
        if (!seen.has(val)) seen.set(val, []);
        seen.get(val).push(r[fkConfig.label_col]);
      });
      const options = Array.from(seen.entries()).map(([val, labels]) => ({
        value: val,
        label: labels.join(', ')
      }));

      return (
        <div key={col.column_name} className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-1">
            {col.column_name}
            <span className="ml-2 text-xs text-gray-500">({col.data_type})</span>
            {!isNullable && <span className="text-red-400 ml-1">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleChange(col.column_name, e.target.value)}
            disabled={isDisabled || loading}
            className="w-full bg-[#1F2937] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#44C2FD] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {isNullable && <option value="">— none —</option>}
            {options.map(({ value: optVal, label }) => (
              <option key={optVal} value={optVal}>
                {label} ({optVal})
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (col.data_type === 'jsonb') {
      return (
        <div key={col.column_name} className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-1">
            {col.column_name}
            <span className="ml-2 text-xs text-gray-500">({col.data_type})</span>
            {!col.is_nullable && <span className="text-red-400 ml-1">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => handleChange(col.column_name, e.target.value)}
            disabled={isDisabled || loading}
            rows={8}
            className={`w-full bg-[#1F2937] border rounded-lg px-3 py-2 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#44C2FD] disabled:opacity-50 disabled:cursor-not-allowed ${
              jsonErrors[col.column_name] ? 'border-red-500' : 'border-gray-700'
            }`}
            placeholder="Enter valid JSON..."
          />
          {jsonErrors[col.column_name] && (
            <p className="text-red-400 text-xs mt-1">{jsonErrors[col.column_name]}</p>
          )}
        </div>
      );
    }

    if (col.data_type === 'text') {
      return (
        <div key={col.column_name} className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-1">
            {col.column_name}
            <span className="ml-2 text-xs text-gray-500">({col.data_type})</span>
            {!col.is_nullable && <span className="text-red-400 ml-1">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => handleChange(col.column_name, e.target.value)}
            disabled={isDisabled || loading}
            rows={3}
            className="w-full bg-[#1F2937] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#44C2FD] disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      );
    }

    return (
      <div key={col.column_name} className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-1">
          {col.column_name}
          <span className="ml-2 text-xs text-gray-500">({col.data_type})</span>
          {!col.is_nullable && <span className="text-red-400 ml-1">*</span>}
        </label>
        <input
          type={col.data_type === 'integer' ? 'number' : 'text'}
          value={value}
          onChange={(e) => handleChange(col.column_name, e.target.value)}
          disabled={isDisabled || loading}
          className="w-full bg-[#1F2937] border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#44C2FD] disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {isDisabled && (
          <p className="text-gray-500 text-xs mt-1">Auto-generated field</p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-[#111827] rounded-lg border border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {isEditing ? `Edit Row (${formatPrimaryKeyDisplay(editingRow)})` : 'Add New Row'}
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors disabled:opacity-50"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {schema.map((col) => renderField(col))}
        </form>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminRowModal;
